import { useEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'

interface EditableTitleProps {
  value: string
  onSave: (next: string) => Promise<void> | void
  canEdit: boolean
  /** Whether the display surface opens the editor on single vs double click. */
  trigger?: 'click' | 'doubleClick'
  /**
   * Pencil placement:
   *  - 'corner' (default): absolute top-right of the title row. Use when
   *    titles live inside a fixed-width container and may truncate (sidebar).
   *  - 'inline': pencil sits right next to the text. Use for centered
   *    titles that shrink-wrap with their content (chart / result titles).
   */
  layout?: 'corner' | 'inline'
  /** className applied to the display span (typography + truncation). */
  className?: string
  /** className applied to the <input> while editing. */
  inputClassName?: string
  /** Optional: pencil-button size in px (default 12). */
  pencilSize?: number
  /** Optional aria-label for the pencil button. */
  ariaLabel?: string
}

export function EditableTitle({
  value,
  onSave,
  canEdit,
  trigger = 'doubleClick',
  layout = 'corner',
  className = '',
  inputClassName = '',
  pencilSize = 12,
  ariaLabel = 'Rename',
}: EditableTitleProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Keep the draft in sync with the prop while not actively editing.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!editing) setDraft(value)
  }, [value, editing])

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  const startEdit = () => {
    if (!canEdit || saving) return
    setDraft(value)
    setEditing(true)
  }

  const commit = async () => {
    const next = draft.trim()
    if (!next || next === value) {
      setEditing(false)
      setDraft(value)
      return
    }
    setSaving(true)
    try {
      await onSave(next)
    } catch {
      setDraft(value)
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            cancel()
          }
          e.stopPropagation()
        }}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        disabled={saving}
        maxLength={200}
        className={inputClassName}
      />
    )
  }

  const displayHandlers =
    trigger === 'doubleClick'
      ? { onDoubleClick: startEdit }
      : { onClick: (e: React.MouseEvent) => { e.stopPropagation(); startEdit() } }

  const pencilButton = canEdit && (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        startEdit()
      }}
      className={
        layout === 'corner'
          ? 'absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-md bg-white shadow-sm ring-1 ring-gray-200 opacity-0 group-hover/edit:opacity-100 text-gray-600 hover:text-gray-900 transition-opacity'
          : 'shrink-0 p-1 rounded-md bg-white shadow-sm ring-1 ring-gray-200 opacity-0 group-hover/edit:opacity-100 text-gray-600 hover:text-gray-900 transition-opacity'
      }
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <Pencil size={pencilSize} />
    </button>
  )

  if (layout === 'inline') {
    return (
      <span className="group/edit inline-flex items-center gap-1 max-w-full align-middle">
        <span className={`min-w-0 ${className}`} {...displayHandlers}>
          {value}
        </span>
        {pencilButton}
      </span>
    )
  }

  return (
    <span className="relative block min-w-0 group/edit">
      <span className={className} {...displayHandlers}>
        {value}
      </span>
      {pencilButton}
    </span>
  )
}
